import React, {ReactElement, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Check from '@mui/icons-material/Check';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Divider from '@mui/material/Divider';
import TimelineIcon from '@mui/icons-material/Timeline';
import {CanvasActionType} from '../../../@types/canvas-action-type';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {useProfiler} from '../../../context/profilerContext';
import {FaultTreeViewType} from '../../../@types/fault-tree-view-type';
import {faultTreeViewRegistry} from '../../../utils/canvas/views';

export default function ViewMenu(): ReactElement {
    const { setCurrentCanvasAction, currentCanvasSelectedNode, lazyLoadDepth, setLazyLoadDepth, loadAllFT, setLoadAllFT, currentFtViewType, setCurrentFtViewType } = useWorkspaceContext();
    const profilerContext = useProfiler();
    const {
        isEnabled: profilerEnabled = false,
        toggle: toggleProfiler = (): void => {},
        openPanel: openProfilerPanel = (): void => {}
    } = profilerContext || {};
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sliderValue, setSliderValue] = useState<number>(lazyLoadDepth);
    const open = Boolean(anchorEl);

    // Get available fault tree views from the registry
    const availableViews = faultTreeViewRegistry.getAvailableViews();
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (): void => {
        setAnchorEl(null);
    };

    // Handler for slider value change (updates local state while dragging)
    const handleSliderChange = (_event: Event, value: number | number[]): void => {
        setSliderValue(value as number);
    };

    // Handler - fires when user releases slider (onChangeCommitted)
    const handleLazyLoadDepthChange = (
        _event: React.SyntheticEvent | Event,
        value: number | number[]
    ): void => {
        const depth = value as number;
        setLazyLoadDepth(depth);
        localStorage.setItem('ftLazyLoadDepth', depth.toString());
        // Trigger immediate reload of current FT
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_RELOAD_FT });
    };

    // Handler for Load All checkbox
    const handleLoadAllChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const checked = event.target.checked;
        setLoadAllFT(checked);
        localStorage.setItem('ftLoadAll', checked.toString());
        // Trigger immediate reload of current FT
        setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_RELOAD_FT });
    };

    // Handler for fault tree view change
    const handleFtViewChange = (viewType: FaultTreeViewType): void => {
        setCurrentFtViewType(viewType);
        localStorage.setItem('ftViewType', viewType);
        // Trigger view switch action to re-render the fault tree
        setCurrentCanvasAction({
            actionType: CanvasActionType.CANVAS_SWITCH_FT_VIEW,
            query: { viewType }
        });
        handleClose();
    };

    return (
        <>
            <Button
                id="view-menu-button"
                aria-controls={open ? 'view-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : undefined}
                aria-label="View Menu"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                style={{ color: 'white', textTransform: 'none', }}
                sx={{
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                }}                
            >
                View
            </Button>
            <Menu
                id="view-menu"
                aria-labelledby="view-menu-button"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'view-menu-button',
                    },
                }}
            >
                <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
                    Fault Tree View
                </Typography>
                {availableViews.map((view) => (
                    <MenuItem
                        key={view.viewType}
                        onClick={(): void => handleFtViewChange(view.viewType)}
                        dense
                        selected={currentFtViewType === view.viewType}
                    >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            {currentFtViewType === view.viewType && <Check fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary={view.displayName} />
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_CALLOUTS }); handleClose();} } dense>Toggle Callouts</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_TAGS }); handleClose();} } dense>Toggle Tags</MenuItem>
                <Divider />
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_RECENTER }); handleClose();} } dense>Recenter</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_FIT_TO_VIEW }); handleClose();} } dense>Fit to View</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_ZOOM_IN }); handleClose();} } dense>Zoom In</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_ZOOM_OUT }); handleClose();} } dense>Zoom Out</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_FULLSCREEN }); handleClose();} } dense>Full Screen</MenuItem>
                <Divider />
                <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                        FT Load Depth: {loadAllFT ? 'All' : sliderValue}
                    </Typography>
                    <Slider
                        size="small"
                        min={1}
                        max={10}
                        value={sliderValue}
                        onChange={handleSliderChange}
                        onChangeCommitted={handleLazyLoadDepthChange}
                        marks
                        valueLabelDisplay="auto"
                        disabled={loadAllFT}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={loadAllFT}
                                onChange={handleLoadAllChange}
                            />
                        }
                        label={<Typography variant="caption">Load All</Typography>}
                    />
                </Box>
                <Divider />
                <MenuItem
                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.NODE_OCCURRENCE_LIST_PARAMS }); handleClose(); }}
                    dense
                    disabled={!currentCanvasSelectedNode}
                >
                    Node Occurrence List
                </MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.INCIDENT_DNA_STATISTICS }); handleClose();} } dense disabled>O-DNA Statistics</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_ODNA_CLEAR_STATS }); handleClose(); }} dense disabled>Clear O-DNA Statistics</MenuItem>
                <Divider />
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.EVENT_SEQUENCE_SENSITIVITY_ANALYSIS }); handleClose(); }} dense>Sensitivity Analysis</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS }); handleClose(); }} dense>Uncertainty Analysis</MenuItem>
                <MenuItem onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.MODEL_TESTING }); handleClose(); }} dense>Model Testing</MenuItem>
                <Divider />
                <MenuItem
                    onClick={(): void => { openProfilerPanel(); handleClose(); }}
                    dense
                >
                    <ListItemIcon>
                        <TimelineIcon fontSize="small" />
                    </ListItemIcon>
                    Show Profiler
                </MenuItem>
                <Box sx={{ px: 2, py: 0.5 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={profilerEnabled}
                                onChange={(): void => toggleProfiler()}
                            />
                        }
                        label={<Typography variant="caption">Enable Profiling</Typography>}
                    />
                </Box>
            </Menu>
        </>
    );
}